<script setup lang="ts">
import { computed, onBeforeMount } from 'vue'
import { storeToRefs } from 'pinia'
import { useHead } from '@unhead/vue'

import { useSourcepointStore } from '../../stores/sourcepoint'
import { usePageStore } from '../../stores/page'
import { useCdnImages } from '../../composables/use-cdn-images'

type AutoNativeMapping = Record<string, string | string[]>

declare global {
  interface Window {
    autoNative?: { mapping: AutoNativeMapping }
  }
}

const { cdnImageUrl } = useCdnImages()
const { consentsExist } = storeToRefs(useSourcepointStore())
const { pageData } = storeToRefs(usePageStore())

const articleMapping: AutoNativeMapping = {
  'ems-native-overallheadline': ['.bx-teaser-dividing-line__text', '#bx-js-ada-native-overallheadline h2'], // Überschrift des Spezials (im Header und in der Breadcrumb)
  'ems-native-backlink': ['.bx-teaser-dividing-line__text a', '#bx-js-ada-native-overallheadline a'], // Verlinkung der Überschrift des Spezial (im Header und in der Breadcrumb)
  'ems-native-subheadline': '.bx-content__roofline', // (optional) Spitzmarke des Artikels
  'ems-native-headline': '.bx-content__headline', // Artikel-Überschrift (im Artikel und im Breadcrumb)
  'ems-native-mainimage': '.bx-content--image-l figure picture img', // Aufmacherbild des Artikels
  'ems-native-imagetext': '.bx-content--image-l figure figcaption .bx-content__media-img-description', // Bildunterschrift des Aufmacherbildes
  'ems-native-copyright': '.bx-content--image-l figure figcaption .bx-content__media-img-credit', // Copyright in der Bildunterschrift des Aufmacherbildes
  'ems-native-headlinetext': '.bx-content__intro p', // Teasertext des Artikels
  'ems-native-articletext': '.bx-content__continuous-text.bx-paragraph-list__html p', // Artikeltext des Artikels
  'ems-native-special-elements': '.bx-content--horizontal-line' // alle Elemente die ausgeblendet werden sollen, wenn es nur einen Artikel gibt
  // 'ems-native-remove-sidebar': '', // wenn im adServer "Sidebar entfernen" ausgewählt wird werden diese Elemente ausgeblendet
  // 'ems-native-content-without-sidebar': '', // definiert die Elemente, die angezeigt werden sollen, wenn im adServer der Kunden-Inhalt als iFrame angelegt wird (z.B. beim Produkt Branded Advertorial)
  // 'ems-native-iframe-hide': '' // Elemente die ausgeblendet werden, wenn im adServer der Kunden-Inhalt als iFrame hochgeladen wird
}

const structuretermMapping: AutoNativeMapping = {
  'ems-native-overallheadline': '#bx-js-ada-native-overallheadline h2' // Überschrift des Spezials (im Header und in der Breadcrumb)
}

const sharedMapping: AutoNativeMapping = {
  'ems-native-adlabel': '.bx-content__advertorial ::before', // Anzeigen-Kennzeichnung im Header
  'ems-native-teaserwrapper': '.bx-teaser-container .bx-teaser-container__wrapper', // Teaserblock am unteren Ende der Seite
  'ems-native-teasercontainer': '.bx-teaser', // ein einzelner Teaser innerhalb des Teaserblocks, die folgenden sechs Elemente müssen im Teaser vorhanden sein:
  'ems-native-teaseradlabel': '.bx-badge.bx-badge--ad.bx-badge--over-image ::before', // Anzeigen-Kennzeichnung im Teaser unter dem Artikel, bzw. auf der Index-Seite
  'ems-native-teasersubheadline': '.bx-teaser__roofline', // Spitzmarke des Artikels (innerhalb des Teaser-Container)
  'ems-native-teaserheadline': '.bx-teaser__headline', // Artikel-Überschrift im Teaser unter dem Artikel
  'ems-native-teaserimage': '.bx-teaser__image picture img', // Teaserbild des Artikels (innerhalb des Teaser-Container)
  'ems-native-teaserlink': '.bx-teaser > a', // Verlinkung auf den Artikel (innerhalb des Teaser-Container), sind Teaser-Container und Teaserlink identisch so setzt man hier <current ein
  'ems-native-teasertext': '.bx-teaser__copy' // (optional) Teasertext des Artikels (innerhalb des Teaser-Container)
}

const mappingsByPageType: Record<string, AutoNativeMapping> = {
  article: articleMapping,
  structureterm: structuretermMapping
}

useHead({
  script: computed(() => consentsExist.value ? [{
    'data-vmid': 'ada-autonative-iframe-height',
    key: 'ada-autonative-iframe-height',
    src: 'https://static.emsservice.de/responsive/setIframeHeightParent.min.js',
    async: true
  }] : [])
})

onBeforeMount(() => {
  document.querySelectorAll('.bx-paragraph-list figure picture source').forEach(source => source.remove())
  document.querySelectorAll('.bx-paragraph-list figure picture img').forEach(img => img.setAttribute('src', cdnImageUrl('/images/autonative-placeholder-16x9.gif')))
  // we need to inject a special headline tag, which is not a stack-standard element
  const specialHeadlineTag = '<div id="bx-js-ada-native-overallheadline" class="bx-content bx-content__continuous-text bx-typo--content-continuous-text"><a href="#LINK-ME-BACK" class="bx-typo--link"><h2>Special-Headline</h2></a></div>'
  // let's do so after the advertorial badge
  document.querySelector('main > span.bx-badge--advertorial')?.insertAdjacentHTML('afterend', specialHeadlineTag)
  window.autoNative = window.autoNative || { mapping: {} }
  window.autoNative.mapping = { ...mappingsByPageType[pageData.value?.pageMeta?.pageType ?? ''], ...sharedMapping }
})
</script>

<template>
  <div />
</template>
